import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { useCurrentPerson } from '../hooks/useCurrentPerson';
import { decorateClient } from '@/db/types/client';
import db from '@/db/db';
import DocumentTemplateModal from './DocumentTemplateModal';
import { MagnifyingGlassIcon, UserIcon } from '@heroicons/react/24/outline';

interface CommandBarItem {
  id: string;
  label: string;
  sublabel?: string;
  icon: string; // path to SVG or 'client' sentinel
  action: () => void;
  /** Optional secondary action rendered as a button on the right */
  secondaryAction?: { label: string; action: () => void };
}

/** Strip non-alphanumeric characters (except spaces) for fuzzy name matching */
function normalizeForSearch(text: string): string {
  return text.replace(/[^a-zA-Z0-9\s]/g, '').toLowerCase();
}

const pages: { label: string; path: string; icon: string }[] = [
  { label: 'Dashboard', path: '/', icon: '/icons/sidebar/home.svg' },
  { label: 'My Clients', path: '/clients', icon: '/icons/sidebar/all-clients.svg' },
  { label: 'Settings', path: '/settings', icon: '/icons/sidebar/settings.svg' },
];

export default function CommandBar() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [newDocClient, setNewDocClient] = useState<{ id: number; slug: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationId } = useCurrentPerson();

  const clients = useLiveQuery(async () => {
    if (!organizationId) return [];
    return db.clients
      .where('organization_id')
      .equals(organizationId)
      .and(c => c.id !== 0)
      .toArray();
  }, [organizationId]);

  // Global Cmd+K / Ctrl+K listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus input when opened, reset state when closed
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      // Small delay to ensure DOM is ready
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const close = useCallback(() => setOpen(false), []);

  // Build filtered results
  const items = useMemo<CommandBarItem[]>(() => {
    const q = query.toLowerCase().trim();
    const results: CommandBarItem[] = [];

    // Pages
    const matchedPages = pages.filter(p => p.label.toLowerCase().includes(q));
    for (const p of matchedPages) {
      results.push({
        id: `page:${p.path}`,
        label: p.label,
        icon: p.icon,
        action: () => { navigate(p.path); close(); },
      });
    }

    // Clients
    if (clients) {
      const decorated = clients.map(decorateClient);
      const normalizedQ = normalizeForSearch(q);
      const matched = q
        ? decorated.filter(c =>
            c.person.displayName.toLowerCase().includes(q) ||
            normalizeForSearch(c.person.displayName).includes(normalizedQ)
          )
        : decorated;

      for (const c of matched.slice(0, 20)) {
        results.push({
          id: `client:${c.label}`,
          label: c.person.displayName,
          sublabel: c.label,
          icon: 'client',
          action: () => { navigate(`/clients/${c.label}`, { state: { from: location.pathname } }); close(); },
          secondaryAction: {
            label: '+ New Document',
            action: () => { setNewDocClient({ id: c.id, slug: c.label }); close(); },
          },
        });
      }
    }

    return results;
  }, [query, clients, navigate, close]);

  // Reset selected index when items change
  useEffect(() => {
    setSelectedIndex(0);
  }, [items.length]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && items[selectedIndex]) {
      e.preventDefault();
      items[selectedIndex].action();
    } else if (e.key === 'Escape') {
      close();
    }
  };

  // Scroll selected item into view
  useEffect(() => {
    const el = listRef.current?.children[selectedIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  return (
    <>
    {/* New Document Modal — triggered from command bar */}
    {newDocClient && (
      <DocumentTemplateModal
        isOpen={true}
        onClose={() => setNewDocClient(null)}
        clientId={newDocClient.id}
        clientSlug={newDocClient.slug}
      />
    )}

    {open && (
    <div className="fixed inset-0 z-[60] flex items-start justify-center pt-[20vh]">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40" onClick={close} />

      {/* Panel */}
      <div className="relative w-full max-w-lg bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            className="flex-1 text-sm bg-transparent outline-none placeholder-gray-400"
            placeholder="Search clients and pages..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium text-gray-400 bg-gray-100 rounded border border-gray-200">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} role="listbox" className="max-h-[300px] overflow-y-auto py-2">
          {items.length === 0 && (
            <p className="px-4 py-6 text-sm text-center text-gray-500">
              No results found
            </p>
          )}

          {items.map((item, i) => (
            <div
              key={item.id}
              role="option"
              aria-selected={i === selectedIndex}
              onClick={item.action}
              onMouseEnter={() => setSelectedIndex(i)}
              className={`w-full flex items-center gap-3 px-4 py-2 text-left text-sm transition-colors cursor-pointer ${
                i === selectedIndex ? 'bg-gray-100' : ''
              }`}
            >
              {item.icon === 'client' ? (
                <UserIcon className="h-4 w-4 text-gray-400 shrink-0" />
              ) : (
                <img src={item.icon} alt="" className="h-4 w-4 shrink-0 brightness-0 opacity-40" />
              )}
              <div className="min-w-0 flex-1">
                <span className="text-gray-900 truncate block">{item.label}</span>
                {item.sublabel && (
                  <span className="text-xs text-gray-400 truncate block">{item.sublabel}</span>
                )}
              </div>
              {item.secondaryAction && (
                <button
                  onClick={(e) => { e.stopPropagation(); item.secondaryAction!.action(); }}
                  className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold text-deep-sea-green hover:bg-deep-sea-green/10 rounded transition-colors shrink-0"
                >
                  {item.secondaryAction.label}
                </button>
              )}
              <kbd className={`hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium text-gray-400 bg-gray-100 rounded border border-gray-200 ${
                i === selectedIndex ? 'visible' : 'invisible'
              }`}>
                ↵
              </kbd>
            </div>
          ))}
        </div>

        {/* Footer with shortcut hints */}
        <div className="flex items-center gap-4 px-4 py-2 border-t border-gray-200 text-[11px] text-gray-400">
          <span className="flex items-center gap-1">
            <kbd className="inline-flex items-center justify-center w-4 h-4 bg-gray-100 rounded border border-gray-200 text-[10px]">↑</kbd>
            <kbd className="inline-flex items-center justify-center w-4 h-4 bg-gray-100 rounded border border-gray-200 text-[10px]">↓</kbd>
            <span className="ml-0.5">Navigate</span>
          </span>
          <span className="flex items-center gap-1">
            <kbd className="inline-flex items-center justify-center h-4 px-1 bg-gray-100 rounded border border-gray-200 text-[10px]">↵</kbd>
            <span className="ml-0.5">Open</span>
          </span>
          <span className="flex items-center gap-1">
            <kbd className="inline-flex items-center justify-center h-4 px-1 bg-gray-100 rounded border border-gray-200 text-[10px]">esc</kbd>
            <span className="ml-0.5">Close</span>
          </span>
        </div>
      </div>
    </div>
    )}
    </>
  );
}
